import VueScrollTo from "vue-scrollto";
import { IContractViewModel } from "../../../../../Scripts/inpulse/I4UViewModels";
import { ValidationErrors } from "../../new-contract-vue";
import { ObjectUtils } from "../../../../../Scripts/inpulse/Common-utils";

export enum SectionMode {
    Hidden,
    Edit,
    Display
}

export enum ActionState {
    Hidden, // Action is not available at all.
    Disabled, // Action is visible, but it is disabled.
    Enabled, // Action is visible and can be triggered.
}

/**
 * Interface for checking section-related stuff.
 */
export interface ISectionChecker {
    /**
     * Checks if all the contract data related to this section has been set.
     * 
     * @param contract
     *     Contract view model object that is checked.
     */
    isAllSet(contract: IContractViewModel): boolean;

    /**
     * Checks if given validation errors collection has any error that is related to this section.
     * 
     * @param validationErrors
     */
    hasErrors(validationErrors: ValidationErrors): boolean;
}

/**
 * Abstract SectionChecker class for convenience. With this it is possible to define list of
 * property accessors and list of validation ids that 'belongs' to a section. Generic checker methods handle the rest.
 */
export class SectionChecker implements ISectionChecker {
    properties: ((contract: IContractViewModel) => any)[];
    idProperties: ((contract: IContractViewModel) => any)[];
    validationIds: string[];

    public isAllSet(contract: IContractViewModel): boolean {
        if (this.properties != null) {
            let len = this.properties.length;
            for (let i = 0; i < len; ++i) {
                let propertyAccessor = this.properties[i];
                let val = propertyAccessor(contract);
                if (!ObjectUtils.isSetNonZero(val))
                    return false;
            }
        }

        if (this.idProperties != null) {
            let len = this.idProperties.length;
            for (let i = 0; i < len; ++i) {
                let propertyAccessor = this.idProperties[i];
                let val = propertyAccessor(contract);
                if (val == null || val == 0)
                    return false;
            }
        }
        return true;
    }
    public hasErrors(validationErrors: ValidationErrors): boolean {
        if (this.validationIds == null)
            return false;
        let len = this.validationIds.length;
        for (let i = 0; i < len; ++i) {
            let id = this.validationIds[i];
            if (validationErrors.has(id))
                return true;
        }
        return false;
    }
}


/**
 * Helper class for easing state management of new contract sections.
 */
export class SectionState {
    name: string;
    previous: SectionState = null;
    next: SectionState = null;
    openEnabled: boolean = false;
    mode: SectionMode = SectionMode.Hidden;
    ref: () => any;
    customerInfoGiven: boolean = false;

    /**
     * @param name
     *    Name of this section, used only for logging purposes at the moment.
     *    
     * @param ref
     *    Function to get Vue ref for this section. This is used to scroll view to correct section when page is reloaded (e.g. in Tupas return). 
     */
    constructor(name: string, ref: () => any) {
        this.name = name;
        this.ref = ref;
    }

    /**
     * Method that initializes this section's mode based on wheter all required data is set. Previous section's mode
     * affects this also. This is used to decide correct section states on page reload.
     * 
     * @param allSet
     *     Boolean value which tells if all of the required section data has been set.
     *     
     * @param previousMode
     *     Forced value to be used as previous section's mode. Used in cases when there is no "real previous section" for this section.
     */
    initializeMode(allSet: boolean, previousMode?: SectionMode) {
        // If section has no real previous sections, it might have "logical" previous section (e.g. customer type selection is
        // "logical previous section" for pre - product selection). Parameter previousMode can be used to force previous section's mode
        // to be used when deciding this section's mode.
        let prevMode = previousMode == null && this.previous != null
            ? this.previous.mode
            : previousMode;
        // First let's check if this section should be in edit or display mode (i.e. is the data completed for it).
        let mode = allSet
            // All data is set, section can be left in display mode.
            ? SectionMode.Display
            // Data is incomplete, let's leave this in edit mode.
            : SectionMode.Edit;

        if (prevMode != null) {
            // If the section was decided to be in edit mode based on its data, BUT its previous state is not completed
            // (i.e. it is in hidden or in edit mode), we will leave this section hidden.
            if (mode == SectionMode.Edit && prevMode != SectionMode.Display)
                mode = SectionMode.Hidden;
        }

        this.mode = mode;

        if ((this.name === "customer" || this.name === "company") && !this.customerInfoGiven) {
            let prepremode = this.previous.previous && this.previous.previous.mode == SectionMode.Display ? true : false;
            if (prepremode && this.previous.mode === SectionMode.Display && this.next.mode === SectionMode.Hidden) {
                this.mode = SectionMode.Edit;
            }
            else {
                this.mode = SectionMode.Hidden;
            }
        }
    }

    scrollTo() {
        console.log("Scrolling to [" + this.name + "]");
        let ref = this.ref();
        if(ref != null)
            VueScrollTo.scrollTo(ref.$el);
    }

    /**
     * Scrolls view to first section that is in edit mode.
     */
    scrollToFirstEdit() {
        let s: SectionState = this;
        if (s.mode == SectionMode.Edit) {
            s.scrollTo();
            return;
        }
        while (s.next) {
            s = s.next;
            if (s.mode == SectionMode.Edit) {
                s.scrollTo();
                break;
            }
        }
    }

    /**
     * Builds 'chain' between given sections, i.e. set's sections' previous and next properties.
     * @param sectionStates
     */
    static formChain(...sectionStates: SectionState[]) {
        let len = sectionStates.length;
        for (let i = 0; i < len; ++i) {
            let curr = sectionStates[i];
            let prev = i > 0 ? sectionStates[i - 1] : null;
            let next = i < len - 1 ? sectionStates[i + 1] : null;
            curr.setSiblings(prev, next);
        }
    }

    setSiblings(previous: SectionState, next: SectionState) {
        this.previous = previous;
        this.next = next;
    }

    proceedActionState() {
        if (this.next != null) {
            return ActionState.Enabled;
            //if (this.next.mode == SectionMode.Hidden)
            //    return ActionState.Enabled;
        }
        return ActionState.Hidden;
    }

    enterModify() {
        if (this.mode != SectionMode.Edit) {
            console.log("Opening section [" + this.name + "]");
            this.mode = SectionMode.Edit;
        }
        this.setPreviousSectionsToMode(this.previous, SectionMode.Display);
        //we don't want to set next modes to Display if it's Hidden since it means that those modes are not edited yet
        this.setNextSectionsToModeIfNotMode(this.next, SectionMode.Display, SectionMode.Hidden);

        VueScrollTo.scrollTo(this.ref().$el);

    }

    //Set previous SectionStates to defined mode
    private setPreviousSectionsToMode(state: SectionState, mode: SectionMode) {
        if (state == null) {
            return;
        }
        state.mode = mode;
        this.setPreviousSectionsToMode(state.previous, mode);
    }

    //Set next SectionStates to defined mode if not specified mode
    private setNextSectionsToModeIfNotMode(state: SectionState, mode: SectionMode, ifNotMode: SectionMode) {
        if (state == null) {
            return;
        }
        if (state.mode != ifNotMode)
        {
            state.mode = mode;
        }
        this.setNextSectionsToModeIfNotMode(state.next, mode, ifNotMode);
    }

    enter() {
        if (this.mode != SectionMode.Edit) {
            console.log("Opening section [" + this.name + "]");
            this.mode = SectionMode.Edit;
        }
        this.scrollTo();
        //VueScrollTo.scrollTo(this.ref().$el);
    }    

    close() {
        if (this.mode == SectionMode.Edit) {
            console.log("Closing section [" + this.name + "]");
            this.mode = SectionMode.Display;
            if (this.name === "customer" || this.name === "company") {
                this.customerInfoGiven = true;
            }
            $("#collapse1-1").removeClass("show");
            $("#collapse1-2").removeClass("show");
            $("#collapse1-3").removeClass("show");
        }
    }

    proceed() {
        this.close();
        if (this.next == null) {
            console.log("Nothing to proceed to!");
            return;
        }
        //if (this.next.mode == SectionMode.Hidden) {
        this.next.enter();
        //}
    }
}
